import { BaseComponent } from './BaseComponent';
import { Announcement } from '../types';
import { Config } from '../core/Config';
import { getTypeIcon } from '../utils/icons';

export class Toast extends BaseComponent {
    private announcement: Announcement;
    private onClose: () => void;
    private onReadMore: () => void;
    private onDismiss: () => void;
    private dismissTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        config: Config,
        announcement: Announcement,
        onClose: () => void,
        onReadMore: () => void,
        onDismiss: () => void
    ) {
        super(config);
        this.announcement = announcement;
        this.onClose = onClose;
        this.onReadMore = onReadMore;
        this.onDismiss = onDismiss;
    }

    protected render(): HTMLElement {
        const toast = this.createElement('div', 'pr-toast pr-sdk');
        toast.setAttribute('role', 'alert');
        toast.setAttribute('aria-live', 'polite');

        const header = this.createElement('div', 'pr-toast-header');

        // Accent-tinted icon tile (outline SVG) to mirror the in-app look.
        const icon = this.createElement('div', 'pr-toast-icon');
        icon.innerHTML = getTypeIcon(this.announcement.type);
        icon.setAttribute('aria-hidden', 'true');
        header.appendChild(icon);

        const content = this.createElement('div', 'pr-toast-content');

        const title = this.createElement('h4', 'pr-toast-title');
        title.textContent = this.announcement.title;
        content.appendChild(title);

        const link = this.createElement('a', 'pr-toast-link');
        link.textContent = this.config.t.readMore;
        link.href = '#';
        link.onclick = (e) => {
            e.preventDefault();
            this.handleReadMore();
        };
        content.appendChild(link);

        header.appendChild(content);

        const closeBtn = this.createElement('button', 'pr-toast-close');
        closeBtn.innerHTML = '&times;';
        closeBtn.setAttribute('aria-label', this.config.t.close);
        closeBtn.onclick = () => this.handleClose();
        header.appendChild(closeBtn);

        toast.appendChild(header);

        // Auto-dismiss after 10 seconds (without marking as read). Keep the
        // handle so hide() can cancel it — otherwise a toast closed early still
        // fires its dismiss callback 10s later, on a component that's gone.
        this.dismissTimer = setTimeout(() => {
            if (this.isVisible()) {
                this.handleAutoDismiss();
            }
        }, 10000);

        return toast;
    }

    public hide(): void {
        if (this.dismissTimer !== null) {
            clearTimeout(this.dismissTimer);
            this.dismissTimer = null;
        }

        super.hide();
    }

    private handleClose(): void {
        this.hide();
        this.onClose();
    }

    private handleAutoDismiss(): void {
        this.hide();
        this.onDismiss();
    }

    private handleReadMore(): void {
        this.onReadMore();
    }
}
